import { all, takeLatest, call, put, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import pickupValidationService, {
  getRolePrefix,
} from '~/services/pickupValidationService';

import {
  PICKUP_VALIDATION_LOAD,
  PICKUP_VALIDATION_START,
  PICKUP_VALIDATION_VERIFY,
  PICKUP_VALIDATION_SUPPORT_RELEASE,
  PICKUP_VALIDATION_RELEASE,
  PICKUP_VALIDATION_CANCEL,
  PICKUP_VALIDATION_UPLOAD_PHOTO,
  PICKUP_VALIDATION_DISPATCHED_RECEIVED,
} from './actionsTypes';

import {
  loadValidationRequest,
  loadValidationSuccess,
  loadValidationFailure,
  startValidationSuccess,
  startValidationFailure,
  verifyValidationSuccess,
  verifyValidationFailure,
  supportReleaseSuccess,
  supportReleaseFailure,
  releaseSuccess,
  releaseFailure,
  cancelValidationSuccess,
  cancelValidationFailure,
  uploadPhotoSuccess,
  uploadPhotoFailure,
  openValidationModal,
} from './actions';

/**
 * Pega o prefixo da role do usuário autenticado.
 * Se não houver role, lança erro pra evitar request inválido.
 */
function* getCurrentRolePrefix() {
  const roles = yield select(state =>
    state.user && state.user.profile ? state.user.profile.roles : []
  );
  const prefix = getRolePrefix(roles);
  if (!prefix) {
    throw new Error('Perfil sem permissão para validação de coleta.');
  }
  return prefix;
}

/**
 * Extrai mensagem amigável de erro da API ou da exceção.
 */
function extractErrorMessage(err, fallback = 'Erro inesperado.') {
  if (err && err.response && err.response.data) {
    const { data } = err.response;

    if (data.message) return data.message;
    if (data.error) return data.error;

    // Erros de validação do Lumen: { field: ["mensagem"] }
    if (typeof data === 'object') {
      const firstKey = Object.keys(data).find(
        k => Array.isArray(data[k]) && data[k][0]
      );
      if (firstKey) return data[firstKey][0];
    }
  }
  if (err && err.message) return err.message;
  return fallback;
}

function* loadValidation({ payload }) {
  const { cartId } = payload;
  try {
    const prefix = yield* getCurrentRolePrefix();
    const { data } = yield call(pickupValidationService.show, prefix, cartId);
    yield put(loadValidationSuccess(data.data || data || null));
  } catch (err) {
    const msg = extractErrorMessage(err, 'Falha ao carregar a validação.');
    yield put(loadValidationFailure(msg));
  }
}

function* startValidation({ payload }) {
  const { cartId } = payload;
  try {
    const prefix = yield* getCurrentRolePrefix();
    const { data } = yield call(pickupValidationService.start, prefix, cartId);
    yield put(startValidationSuccess(data.data));
  } catch (err) {
    const msg = extractErrorMessage(err, 'Falha ao iniciar a validação.');
    yield put(startValidationFailure(msg));
    toast.error(msg);
  }
}

function* verifyValidation({ payload }) {
  const { cartId, data: verifyPayload } = payload;
  try {
    const prefix = yield* getCurrentRolePrefix();
    const { data } = yield call(
      pickupValidationService.verify,
      prefix,
      cartId,
      verifyPayload
    );
    yield put(verifyValidationSuccess(data.data));

    if (data.data.status === 'awaiting_dispatch') {
      toast.success(
        'Dados confirmados. Aguarde o entregador acionar a coleta.'
      );
    } else if (data.data.status === 'blocked') {
      toast.error(
        'Tentativas esgotadas. Não entregue a mercadoria — acione o suporte Viddy.'
      );
    } else if (data.data.status === 'failed') {
      toast.warning(
        `Dados não conferem. Tentativas restantes: ${data.data.attempts_remaining}`
      );
    }
  } catch (err) {
    const msg = extractErrorMessage(err, 'Falha na validação.');
    yield put(verifyValidationFailure(msg));
    toast.error(msg);

    if (
      msg.includes('não está mais aceitando') ||
      msg.includes('Limite de tentativas')
    ) {
      yield put(loadValidationRequest(cartId));
    }
  }
}

function* supportRelease({ payload }) {
  const { cartId } = payload;
  try {
    // Rota fixa sob /support — não usa o prefixo da role do operador.
    const { data } = yield call(pickupValidationService.supportRelease, cartId);
    yield put(supportReleaseSuccess(data.data));
    toast.success('Coleta liberada pelo suporte.');
  } catch (err) {
    const msg = extractErrorMessage(err, 'Falha ao liberar a coleta.');
    yield put(supportReleaseFailure(msg));
    toast.error(msg);
  }
}

function* releaseValidation({ payload }) {
  const { cartId } = payload;
  try {
    const prefix = yield* getCurrentRolePrefix();
    const { data } = yield call(
      pickupValidationService.release,
      prefix,
      cartId
    );
    yield put(releaseSuccess(data.data));
    toast.success('Pedido entregue ao entregador.');
  } catch (err) {
    const msg = extractErrorMessage(err, 'Falha ao liberar o pedido.');
    yield put(releaseFailure(msg));
    toast.error(msg);
  }
}

function* cancelValidation({ payload }) {
  const { cartId, reason } = payload;
  try {
    const prefix = yield* getCurrentRolePrefix();
    const { data } = yield call(
      pickupValidationService.cancel,
      prefix,
      cartId,
      reason
    );
    yield put(cancelValidationSuccess(data.data));
    toast.info('Sessão de validação cancelada.');
  } catch (err) {
    const msg = extractErrorMessage(err, 'Falha ao cancelar a sessão.');
    yield put(cancelValidationFailure(msg));
    toast.error(msg);
  }
}

function* uploadPhoto({ payload }) {
  const { cartId, file } = payload;
  try {
    const prefix = yield* getCurrentRolePrefix();
    const { data } = yield call(
      pickupValidationService.uploadPhoto,
      prefix,
      cartId,
      file
    );
    yield put(uploadPhotoSuccess(data.data));
    toast.success('Foto da retirada salva.');
  } catch (err) {
    const msg = extractErrorMessage(err, 'Falha ao enviar a foto.');
    yield put(uploadPhotoFailure(msg));
    toast.error(msg);
  }
}

function* onDispatchedReceived({ payload }) {
  console.log('[onDispatchedReceived] payload:', payload);

  const currentValidation = yield select(
    state => state.pickupValidation.validation
  );

  // Anomalia: entregador acionou coleta sem validação prévia.
  // Garante dois caminhos pro operador ver o aviso:
  //   1) Toast bloqueante (visível em QUALQUER tela do sistema)
  //   2) Abre o modal de validação na Sale page (se ela estiver montada)
  if (payload.skipped_validation) {
    const orderNumber = payload.order_number || '';
    const orderRef = orderNumber ? ` do pedido #${orderNumber}` : '';

    toast.error(
      `O fluxo de entrega${orderRef} não foi realizado de forma correta, este pedido não contará com o seguro de entrega.`,
      {
        autoClose: 15000,
        closeOnClick: true,
        draggable: true,
        pauseOnHover: true,
        toastId: `pickup-anomaly-${payload.validation_id}`,
      }
    );
    yield put(openValidationModal());
    return;
  }

  // Fluxo normal: se for a sessão que o modal está mostrando, recarrega
  if (currentValidation && currentValidation.id === payload.validation_id) {
    yield put(
      loadValidationRequest(currentValidation.cart_id || payload.cart_id)
    );
  }
}

export default all([
  takeLatest(PICKUP_VALIDATION_LOAD, loadValidation),
  takeLatest(PICKUP_VALIDATION_START, startValidation),
  takeLatest(PICKUP_VALIDATION_VERIFY, verifyValidation),
  takeLatest(PICKUP_VALIDATION_SUPPORT_RELEASE, supportRelease),
  takeLatest(PICKUP_VALIDATION_RELEASE, releaseValidation),
  takeLatest(PICKUP_VALIDATION_CANCEL, cancelValidation),
  takeLatest(PICKUP_VALIDATION_UPLOAD_PHOTO, uploadPhoto),
  takeLatest(PICKUP_VALIDATION_DISPATCHED_RECEIVED, onDispatchedReceived),
]);
